import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { getCategories } from "../utils/api";
import "./Header.css";
import logo from "../assets/images/logo.png";

export default function Header() {
  const { totalItems } = useCart();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catOpen, setCatOpen] = useState(false);

  const catRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  // scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location]);

  // fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategories();
        setCategories(response?.data?.data || []);
      } catch (err) {
        console.error("Failed to load categories:", err);
      }
    };

    fetchCategories();
  }, []);

  // close dropdown outside click
  useEffect(() => {
    const handler = (e) => {
      if (catRef.current && e.target instanceof Node && !catRef.current.contains(e.target)) {
        setCatOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  const goToCategory = (cat) => {
    const params = new URLSearchParams({
      categoryId: cat.id,
      categoryName: cat.name,
    });

    navigate(`/catalog?${params.toString()}`);
    setCatOpen(false);
  };

  return (
    <header className={`header ${scrolled ? "scrolled" : ""}`}>
      <div className="header-inner container">
        <Link to="/" className="logo">
          <div className="logo-icon-wrap">
            <img src={logo} alt="zoriplace logo"></img>
          </div>
        </Link>

        <nav className={`nav ${menuOpen ? "open" : ""}`}>
          <div className="nav-dropdown" ref={catRef}>
            <button
              type="button"
              className={`nav-dropdown-trigger ${catOpen ? "active" : ""}`}
              onClick={() => setCatOpen((prev) => !prev)}
            >
              Каталог
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                style={{
                  width: 11,
                  height: 11,
                  marginLeft: 4,
                  transition: "transform 0.2s",
                  transform: catOpen ? "rotate(180deg)" : "none",
                }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {catOpen && (
              <div className="nav-dropdown-menu">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className="nav-dropdown-item"
                    onClick={() => {
                      if (cat.name === "Індивідуальне замовлення") {
                        navigate("/individual-order");
                        setCatOpen(false);
                        return;
                      }
                      goToCategory(cat);
                    }}
                  >
                    <span>{cat.name}</span>
                  </button>
                ))}

                {categories.length > 0 && <div className="nav-dropdown-divider" />}

                <button
                  type="button"
                  className="nav-dropdown-item nav-dropdown-all"
                  onClick={() => {
                    navigate("/catalog");
                    setCatOpen(false);
                  }}
                >
                  Весь каталог →
                </button>
              </div>
            )}
          </div>

          <NavLink to="/faq">Відповіді</NavLink>

          <NavLink to="/delivery">Доставка і оплата</NavLink>

          <NavLink to="/certificate">Сертифікати</NavLink>

          <NavLink to="/contact">Контакти</NavLink>
        </nav>

        <div className="header-actions">
          <button
            type="button"
            className="header-icon-btn"
            onClick={() => navigate("/catalog")}
            aria-label="Пошук"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </button>

          <Link to="/cart" className="cart-btn" aria-label="Кошик">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>

            {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
          </Link>

          <button
            type="button"
            className={`burger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Меню"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
    </header>
  );
}
